import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { BrandLiveReport, listBrandLiveReports, respondBrandLiveReport } from '../../api/payment/brand-live.api';
import { canRespond, reportStatusLabel, validateResponse } from '../../utils/brand-live';
import { IndividualOrdererBadge } from '../brand-deal/orderer-badge';

// Danh sách báo cáo gian lận, dùng chung: creator thấy phần giải trình của mình, doanh nghiệp thấy phản hồi của creator.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '');

const statusClass = (s: string) =>
  s === 'Upheld' ? 'text-red-500' : s === 'Dismissed' ? 'text-green-600' : 'text-amber-500';

const BrandLiveReportList: React.FC<{ role: 'creator' | 'business'; refreshKey?: number }> = ({ role, refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<BrandLiveReport[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const lock = useRef(false);

  const load = useCallback(() => {
    listBrandLiveReports(role)
      .then(setReports)
      .catch((err) => toast.error(errorMessage(err, 'Không tải được báo cáo gian lận.')))
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const respond = async (r: BrandLiveReport) => {
    if (lock.current) return;
    const text = (drafts[r.id] ?? '').trim();
    const invalid = validateResponse(text);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    lock.current = true;
    setBusyId(r.id);
    try {
      const updated = await respondBrandLiveReport(r.id, text);
      setReports((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
      toast.success('Đã gửi giải trình.');
    } catch (err) {
      toast.error(errorMessage(err, 'Không gửi được giải trình.'));
    } finally {
      lock.current = false;
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }
  if (reports.length === 0) return <p className="ui-muted text-[14px]">Chưa có báo cáo gian lận nào.</p>;

  return (
    <div className="flex flex-col gap-3">
      {reports.map((r) => (
        <div key={r.id} className="ui-card flex flex-col gap-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="ui-heading text-[14px] font-semibold">
              {role === 'creator' ? r.businessName : r.creatorName}{role === 'creator' && <IndividualOrdererBadge kind={r.ordererKind} className="ml-1" />} · buổi live {fmtTime(r.sessionStartedAt)}
            </div>
            <span className={`text-[13px] font-semibold ${statusClass(r.status)}`}>{reportStatusLabel(r.status)}</span>
          </div>
          <p className="ui-text whitespace-pre-line text-[13px]">
            <span className="font-semibold">Lý do báo: </span>
            {r.reason}
          </p>
          {r.creatorResponse && (
            <p className="ui-text whitespace-pre-line text-[13px]">
              <span className="font-semibold">Creator giải trình: </span>
              {r.creatorResponse}
            </p>
          )}
          {r.status === 'Open' && !r.creatorResponse && (
            <div className="ui-muted text-[13px]">Hạn giải trình {fmtTime(r.responseDueAt)}</div>
          )}
          {r.status !== 'Open' && r.decisionReason && (
            <p className="ui-text whitespace-pre-line text-[13px]">
              <span className="font-semibold">Admin kết luận: </span>
              {r.decisionReason}
            </p>
          )}
          {r.status === 'Upheld' && r.stopDeal && <div className="text-[13px] text-red-500">Quảng cáo đã dừng do gian lận.</div>}
          {role === 'creator' && canRespond(r) && (
            <div className="flex flex-col gap-2">
              <textarea
                className="ui-input min-h-[80px] w-full px-3 py-2 text-[13px]"
                placeholder="Giải trình của bạn (chỉ gửi được một lần)"
                value={drafts[r.id] ?? ''}
                maxLength={2000}
                onChange={(e) => setDrafts((p) => ({ ...p, [r.id]: e.target.value }))}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => respond(r)}
                  className="rounded-lg bg-[#0057FF] px-3 py-2 text-[13px] font-semibold text-white disabled:opacity-60"
                >
                  Gửi giải trình
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default BrandLiveReportList;
